import { useEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';
import { trace, SpanStatusCode } from '@opentelemetry/api';
import { LOCAL_STORAGE_KEYS } from 'constant/localStorage';
import { Logger } from 'utils/logger';

// Mirrors the normalization in webVitals.ts — numeric segments become :id so
// that /projects/123 and /projects/456 group under /projects/:id in SigNoz
// rather than creating a unique series per record.
const normalizePath = (path: string) =>
  path
    .replace(/\?.*$/, '') // strip query string
    .replace(
      /\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi,
      '/:id',
    ) // UUID segments
    .replace(/\/\d+/g, '/:id'); // numeric segments

const navigationTracer = trace.getTracer('navigation');

/**
 * Tracks SPA route changes as OpenTelemetry spans.
 *
 * WHY THIS EXISTS
 * DocumentLoadInstrumentation only fires on full page loads. In a React Router
 * SPA, client-side navigation between routes generates zero telemetry — no new
 * document load occurs. This hook fills that gap by creating a 'navigation' span
 * on every location change detected by React Router.
 *
 * WHAT IS CAPTURED PER NAVIGATION
 *   navigation.from         — normalized previous path (e.g. /projects/:id)
 *   navigation.to           — normalized current path
 *   navigation.type         — PUSH (link click) | POP (back/forward button) | REPLACE (redirect)
 *   navigation.from.raw     — raw previous path (with real IDs, for debugging)
 *   navigation.to.raw       — raw current path
 *   page.title              — document.title at the moment the navigation settled
 *   user.id                 — current user from localStorage (matches DynamicAttributeSpanProcessor)
 *
 * PLACEMENT
 * Must be called inside a component that is rendered within the React Router
 * context (i.e. inside RouterProvider). PrivateRoute is the right place because
 * all authenticated routes render through it, and authenticated navigation is
 * what matters for user journey analysis.
 *
 * The initial render (first mount) is intentionally skipped — DocumentLoad
 * instrumentation already captures the first page entry.
 */
export const useNavigationTracker = () => {
  const location = useLocation();
  const navigationType = useNavigationType(); // PUSH | POP | REPLACE
  const prevRawPathRef = useRef<string | null>(null);

  useEffect(() => {
    const rawTo = location.pathname;

    // Skip initial mount — DocumentLoadInstrumentation handles the first entry.
    if (prevRawPathRef.current === null) {
      prevRawPathRef.current = rawTo;
      return;
    }

    const rawFrom = prevRawPathRef.current;

    // Skip if the path did not change (e.g. only the hash or search changed).
    // Avoids noise from scroll-to-anchor links and filter updates that push
    // to history without actually navigating to a new page.
    if (rawFrom === rawTo) {
      return;
    }

    const normalizedFrom = normalizePath(rawFrom);
    const normalizedTo = normalizePath(rawTo);

    Logger.log({
      message: 'Navigation detected',
      enableInProduction: true,
      data: {
        normalizedFrom,
        normalizedTo,
        rawFrom,
        rawTo,
      },
    });

    const span = navigationTracer.startSpan('navigation', {
      attributes: {
        'navigation.from': normalizedFrom,
        'navigation.to': normalizedTo,
        // Raw paths retained for debugging — useful when :id normalization makes
        // it unclear which specific record the user navigated to.
        'navigation.from.raw': rawFrom,
        'navigation.to.raw': rawTo,
        // PUSH = forward navigation (link/button click)
        // POP  = browser back/forward button
        // REPLACE = programmatic redirect (e.g. after login, after form submit)
        'navigation.type': navigationType,
        'page.title': document.title,
        'user.id':
          localStorage.getItem(LOCAL_STORAGE_KEYS.USER_ID) ?? 'UnknownUser',
      },
    });

    span.setStatus({ code: SpanStatusCode.OK });
    // Navigation in a SPA is instantaneous from the user's perspective —
    // end the span immediately. Duration is not meaningful here; the value
    // is in the attributes, not the timing.
    span.end();

    prevRawPathRef.current = rawTo;
  }, [location.pathname, navigationType]);
};
